
// App.js - Squarespace Design with Fixed Z-Index for Burger Menu
import React, { useEffect, useState, useRef } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom';
import { auth } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from './firebase';
import './App.css';

// MemorialPage stays in the main bundle: shared links land there and it
// should paint without a second round-trip. Login/signup are tiny and
// common. Everything else loads on demand — visitors reading a memorial
// no longer download the dashboard, forms, blog, and admin pages.
import MemorialPage from './MemorialPage';
import Login from './Login';
import Signup from './Signup';
import ForgotPassword from './ForgotPassword';
import Footer from './components/Footer';
import MetaTagManager from './MetaTagManager';
import ScrollToTop from './ScrollToTop';
import SoulSearch from './components/SoulSearch';
import { initAnalytics, identify, trackPageView } from './analytics';

const MemorialForm = React.lazy(() => import('./MemorialForm'));
const QuickCreate = React.lazy(() => import('./QuickCreate'));
const Dashboard = React.lazy(() => import('./Dashboard'));
const EditMemorialForm = React.lazy(() => import('./EditMemorialForm'));
const MemorialSelector = React.lazy(() => import('./MemorialSelector'));
const SponsoredPage = React.lazy(() => import('./SponsoredPage'));
const AccountSettings = React.lazy(() => import('./AccountSettings'));
const AccountSecurity = React.lazy(() => import('./AccountSecurity'));
const PaymentSetup = React.lazy(() => import('./PaymentSetup'));
const DonationDashboard = React.lazy(() => import('./DonationDashboard'));
const SubscriptionPage = React.lazy(() => import('./SubscriptionPage'));
const FAQ = React.lazy(() => import('./FAQ'));
const Help = React.lazy(() => import('./Help'));
const About = React.lazy(() => import('./About'));
const SearchResultsPage = React.lazy(() => import('./SearchResultsPage'));
const Terms = React.lazy(() => import('./legal/Terms'));
const Privacy = React.lazy(() => import('./legal/Privacy'));
const DonationAgreement = React.lazy(() => import('./legal/DonationAgreement'));
const CommunityGuidelines = React.lazy(() => import('./legal/CommunityGuidelines'));
const BlogList = React.lazy(() => import('./BlogList'));
const BlogPost = React.lazy(() => import('./BlogPost'));
const BlogAdmin = React.lazy(() => import('./BlogAdmin'));
const CommunityList = React.lazy(() => import('./CommunityList'));
const CommunityPost = React.lazy(() => import('./CommunityPost'));
const CommunityAdmin = React.lazy(() => import('./CommunityAdmin'));
import { iapAvailable, iapLogIn, configureIap } from './iap';

// Galaxy view is heavy (Three.js) — lazy-load it so the map pays nothing
// Inside the native app, forbid zooming: iOS auto-zooms the web view when a
// text field is focused and never zooms back, leaving the page cropped. Apps
// have fixed frames; the website in Safari keeps normal zoom for accessibility.
if (typeof window !== 'undefined' && (window.Capacitor || /SouleMapApp/i.test(navigator.userAgent || ''))) {
  const vp = document.querySelector('meta[name="viewport"]');
  if (vp) {
    vp.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
  }
  // Marks the native shell so CSS can correct for it. With viewport-fit=cover
  // the web page lays out against the FULL screen, so 100vh includes the strip
  // behind the status bar and the home indicator — while the native scroll
  // view insets those same strips away. The page therefore ends up taller than
  // the area it can occupy, and the difference is scrollable: enough to drag
  // the fixed header up off the screen. See .sm-native in App.css.
  document.documentElement.classList.add('sm-native');
}

const GalaxyView = React.lazy(() => import('./GalaxyView'));

// The single loading moment: a beating soul-star over the night sky.
// Shown while the galaxy loads AND held for a few seconds so the moment lands,
// then faded away to reveal the already-living galaxy beneath.
function GalaxyVeil({ fading }) {
  // A living night: twinkling dust and the occasional shooting star.
  const veilStars = React.useMemo(() => {
    const dots = [];
    for (let i = 0; i < 44; i++) {
      dots.push({
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: 1 + Math.random() * 2.2,
        delay: Math.random() * 4,
        duration: 2.4 + Math.random() * 3,
        tint: Math.random() < 0.75 ? '#ffffff' : (Math.random() < 0.5 ? '#b9cdff' : '#ffe4b9'),
      });
    }
    const shooters = [
      { top: 18, left: 78, delay: 0.6, duration: 3.4 },
      { top: 40, left: 92, delay: 2.1, duration: 3.0 },
      { top: 8,  left: 55, delay: 3.6, duration: 3.8 },
    ];
    return { dots, shooters };
  }, []);

  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center overflow-hidden"
      style={{
        background: 'radial-gradient(ellipse at 50% 46%, #16121f 0%, #0d0b16 75%)',
        zIndex: 99999,
        opacity: fading ? 0 : 1,
        transition: 'opacity 1.1s ease',
        pointerEvents: fading ? 'none' : 'auto',
      }}
    >
      {veilStars.dots.map((d, i) => (
        <span
          key={`vd${i}`}
          className="veil-star"
          style={{
            left: `${d.left}%`,
            top: `${d.top}%`,
            width: `${d.size}px`,
            height: `${d.size}px`,
            background: d.tint,
            animationDelay: `${d.delay}s`,
            animationDuration: `${d.duration}s`,
          }}
        />
      ))}
      {veilStars.shooters.map((sh, i) => (
        <span
          key={`vs${i}`}
          className="veil-shooter"
          style={{
            top: `${sh.top}%`,
            left: `${sh.left}%`,
            animationDelay: `${sh.delay}s`,
            animationDuration: `${sh.duration}s`,
          }}
        />
      ))}
      <span
        className="soul-heartbeat text-3xl mb-6"
        style={{ color: '#ffe2a8', textShadow: '0 0 24px rgba(255,215,140,0.75), 0 0 60px rgba(255,215,140,0.35)' }}
      >
        ✦
      </span>
      <p className="soul-heartbeat-text text-white/70 text-sm tracking-[0.3em] uppercase font-light">
        Entering the Galaxy of Memorials
      </p>
    </div>
  );
}


// Reports every in-app navigation to GA4. A single-page app changes the URL
// without reloading, so without this only the first page of a visit is seen —
// which makes real journeys ("landed on a memorial → signed up") invisible.
function AnalyticsRouteTracker() {
  const location = useLocation();
  const first = React.useRef(true);
  React.useEffect(() => {
    // The tag in index.html already counted the landing page. Skipping the
    // first render keeps that fallback intact without double-counting.
    if (first.current) { first.current = false; return; }
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

function MountSignal({ onMount }) {
  React.useEffect(() => { onMount(); }, [onMount]);
  return null;
}

// The veil is a first-impression: it plays once per visit — not on every
// navigation back to the galaxy, and not after page reloads (login flows,
// back buttons). sessionStorage survives reloads; it resets only when the
// tab or app is truly closed and reopened.
let galaxyVeilPlayed = false;
const VEIL_KEY = 'soulemap_veil_played';

function veilAlreadyPlayed() {
  if (galaxyVeilPlayed) return true;
  try { return window.sessionStorage.getItem(VEIL_KEY) === '1'; } catch (e) { return false; }
}

function markVeilPlayed() {
  galaxyVeilPlayed = true;
  try { window.sessionStorage.setItem(VEIL_KEY, '1'); } catch (e) { /* private mode */ }
}

function GalaxyEntry({ user }) {
  const [withVeil] = React.useState(() => !veilAlreadyPlayed());
  const [loaded, setLoaded] = React.useState(false);   // galaxy actually ready
  const [minHold, setMinHold] = React.useState(false); // 5s of heartbeat elapsed
  const [fading, setFading] = React.useState(false);
  const [gone, setGone] = React.useState(false);
  const handleLoaded = React.useCallback(() => setLoaded(true), []);

  React.useEffect(() => { markVeilPlayed(); }, []);

  React.useEffect(() => {
    const t = setTimeout(() => setMinHold(true), 2000);
    return () => clearTimeout(t);
  }, []);

  // Reveal only when BOTH are true: the galaxy is built AND the moment has
  // had its five seconds. Whichever finishes last starts the fade.
  React.useEffect(() => {
    if (loaded && minHold && !fading) {
      setFading(true);
      const t = setTimeout(() => setGone(true), 1200);
      return () => clearTimeout(t);
    }
  }, [loaded, minHold, fading]);

  return (
    <>
      <React.Suspense fallback={null}>
        <GalaxyView user={user} />
        <MountSignal onMount={handleLoaded} />
      </React.Suspense>
      {withVeil && !gone && <GalaxyVeil fading={fading} />}
    </>
  );
}
const Forever = React.lazy(() => import('./Forever'));
const ForeverComplete = React.lazy(() => import('./ForeverComplete'));

function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showBurgerMenu, setShowBurgerMenu] = useState(false);
  const [_userHasMemorials, setUserHasMemorials] = useState(false);
  const [mapSearchTerm, setMapSearchTerm] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [showDonationsSubmenu, setShowDonationsSubmenu] = useState(false);
  const [showSettingsSubmenu, setShowSettingsSubmenu] = useState(false);
  const searchInputRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Helper function to get user display name
  const getUserDisplayName = (user) => {
    if (!user) return 'User';
    return user.displayName || user.email?.split('@')[0] || 'User';
  };

  // Helper function to get user initials for avatar
  const getUserInitials = (user) => {
    if (!user) return 'U';
    
    if (user.displayName) {
      // If display name exists, use first letter of first and last name
      const names = user.displayName.split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return user.displayName[0].toUpperCase();
    }
    
    // Fall back to first letter of email
    return user.email?.[0].toUpperCase() || 'U';
  };

  // Analytics: one-time setup (platform property, error reporting).
  useEffect(() => { initAnalytics(); }, []);

  // auth listener
  useEffect(() => {
    return onAuthStateChanged(auth, u => {
      setUser(u);
      setAuthChecked(true);
      identify(u ? u.uid : null);
      // In the iOS shell, tie Apple purchases to this account so the
      // RevenueCat webhook can grant entitlements to the right user.
      if (iapAvailable()) {
        if (u) iapLogIn(u.uid); else configureIap();
      }
    });
  }, []);

  // Check if user has memorials
  useEffect(() => {
    if (!user) {
      setUserHasMemorials(false);
      return;
    }

    const q = query(
      collection(db, 'memorials'),
      where('createdBy', '==', user.uid)
    );
    
    const unsubscribe = onSnapshot(q, (snap) => {
      setUserHasMemorials(snap.docs.length > 0);
    }, (error) => {
      console.error('Error checking user memorials:', error);
      setUserHasMemorials(false);
    });

    return () => unsubscribe();
  }, [user]);

  // click-outside to close dropdown
  useEffect(() => {
    const onClick = e => {
      if (showDropdown && !e.target.closest('.dropdown-container')) {
        setShowDropdown(false);
        setShowDonationsSubmenu(false); // Reset donations submenu
        setShowSettingsSubmenu(false); // Reset settings submenu
      }
      if (showBurgerMenu && !e.target.closest('.burger-menu-container')) {
        setShowBurgerMenu(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showDropdown, showBurgerMenu]);

  // Focus search input when activated
  useEffect(() => {
    if (searchActive && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchActive]);

  // page flags
  const isLogin     = location.pathname === '/login';
  const isSignup    = location.pathname === '/signup';
  const isForgotPassword = location.pathname === '/forgot-password';
  const isMemorial  = location.pathname.startsWith('/memorial/');
  const isDashboard = location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/');
  const isCreate    = location.pathname === '/create-memorial' || location.pathname === '/create-memorial/full';
  const isForever   = location.pathname === '/forever' || location.pathname.startsWith('/forever/');
  const isEdit      = location.pathname.startsWith('/edit/') || location.pathname.startsWith('/edit-memorial/');
  const isSelectMemorial = location.pathname === '/select-memorial';
  const isAccountSettings = location.pathname === '/account-settings';
  const isAccountSecurity = location.pathname === '/account-security';
  const isPaymentSetup = location.pathname === '/payment-setup';
  const isDonations = location.pathname === '/donations';
  const isSubscription = location.pathname === '/subscription';
  const isMap = false; // Earth view removed — the Galaxy of Memorials is the only view
  const isGalaxy = location.pathname === '/galaxy' || location.pathname === '/';
  const isFAQ = location.pathname === '/faq';
  const isHelp = location.pathname === '/help';
  const isAbout = location.pathname === '/about';
  const isSearch = location.pathname === '/search';
  const isBlog = location.pathname === '/blog' || location.pathname.startsWith('/blog/');
  const isBlogAdmin = location.pathname === '/blog-admin' || location.pathname.startsWith('/blog-admin/');
  const isCommunity = location.pathname === '/community' || location.pathname.startsWith('/community/');
  const isCommunityAdmin = location.pathname === '/community-admin';

  const hideWrapper = isLogin || isSignup || isForgotPassword || isMemorial || isDashboard || isCreate || isForever || isEdit || isSelectMemorial || isAccountSettings || isAccountSecurity || isPaymentSetup || isDonations || isSubscription || isMap || isGalaxy || isFAQ || isHelp || isAbout || isSearch || isBlog || isBlogAdmin || isCommunity || isCommunityAdmin;

  // Determine if footer should be shown
  const showFooter = isFAQ || isHelp || isAbout || isBlog || isCommunity;

  // Meta tag management for route changes
  useEffect(() => {
    // Reset meta tags to default when navigating to non-memorial pages
    if (!isMemorial) {
      MetaTagManager.resetToDefault();
    }
  }, [location.pathname, isMemorial]);

  if (!authChecked) return null;

  // logout & dropdown
  const handleLogout = async () => {
    await signOut(auth);
    setShowDropdown(false);
    navigate('/map');
  };

  // Helper function to create login/signup URLs with redirect
  const getAuthUrl = (path) => {
    if (isMemorial) {
      return `${path}?redirect=${encodeURIComponent(location.pathname)}`;
    }
    return path;
  };

  // Handle map reset
  const _handleMapReset = () => {
    setMapSearchTerm('');
    setSearchActive(false);
    // Call the reset function on MapView if it exists
    if (window.resetMapView) {
      window.resetMapView();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes slide-left {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .animate-slide-left {
          animation: slide-left 0.3s ease-out forwards;
        }
        
        /* Ensure menus are always on top */
        .side-drawer {
          z-index: 99999 !important;
        }
        
        .backdrop {
          z-index: 99998 !important;
        }
      `}} />
      {/* Show header on all pages except login, signup, and forgot password */}
      {!isLogin && !isSignup && !isForgotPassword && (
        <header className="fixed top-0 left-0 right-0 bg-black border-b border-gray-900 shadow-sm" style={{ zIndex: 99997 }}>
          <div className="flex items-center justify-between h-20 md:h-24 px-6 md:px-10 lg:px-16">
            {/* Logo - Updated with Squarespace styling */}
<Link
  to="/"
  className={`flex items-center flex-shrink-0 h-full transition-all duration-300 ${
    searchActive && isMap ? 'md:opacity-100 opacity-0 md:visible invisible md:w-auto w-0' : ''
  }`}
>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center flex-shrink-0">
                  <img 
                    src="/bird.png" 
                    alt="SouleMap" 
                    className="h-6 md:h-8" 
                  />
                </div>
                <div>
                  <h1 className="text-white font-extralight text-xl md:text-2xl leading-tight tracking-wide">SouleMap</h1>
                  <p className="text-gray-500 text-[10px] md:text-xs font-normal leading-tight tracking-widest uppercase">The Social Memorial</p>
                </div>
              </div>
            </Link>

            {/* Search - Squarespace style */}
            {isMap && (
              <div className={`flex items-center md:flex-1 transition-all duration-300 ${
                searchActive ? 'flex-1 justify-start md:justify-end' : 'justify-end'
              }`}>
                <div className={`relative transition-all duration-300 ${
                  searchActive ? 'w-full md:w-auto' : ''
                }`}>
                  {/* Search Icon Button */}
                  {!searchActive && (
                    <div className="flex justify-end">
                      <button
                        onClick={() => setSearchActive(true)}
                        className="group px-6 py-3 flex items-center gap-3 transition-all duration-300 hover:opacity-70"
                      >
                        <svg 
                          className="w-4 h-4 text-white transition-all duration-300 group-hover:scale-110" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                          strokeWidth={1}
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                          />
                        </svg>
                        <span className="text-white text-xs uppercase tracking-widest font-normal">Search</span>
                      </button>
                    </div>
                  )}

                  {/* Expanded Search Field */}
                  {searchActive && (
                    <form 
                      onSubmit={(e) => { 
                        e.preventDefault(); 
                        if (mapSearchTerm.trim()) {
                          navigate(`/search?q=${encodeURIComponent(mapSearchTerm.trim())}`);
                          setSearchActive(false);
                          setMapSearchTerm('');
                        }
                      }} 
                      className="flex items-center justify-end md:justify-start"
                      role="search"
                    >
                      <div className="relative flex items-center animate-slide-left">
                        <svg 
                          className="absolute left-4 w-4 h-4 text-gray-500 pointer-events-none" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                          strokeWidth={1}
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                          />
                        </svg>
                        
                        <input
                          ref={searchInputRef}
                          type="search"
                          inputMode="search"
                          enterKeyHint="search"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck="false"
                          value={mapSearchTerm}
                          onChange={e => setMapSearchTerm(e.target.value)}
                          onKeyDown={(e) => {
                            // Submit on Enter key
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (mapSearchTerm.trim()) {
                                navigate(`/search?q=${encodeURIComponent(mapSearchTerm.trim())}`);
                                setSearchActive(false);
                                setMapSearchTerm('');
                              }
                            }
                          }}
                          onBlur={() => {
                            if (!mapSearchTerm) {
                              setTimeout(() => setSearchActive(false), 200);
                            }
                          }}
                          placeholder="Search memorials..."
                          className="pl-12 pr-10 py-3 w-[calc(100vw-8rem)] sm:w-72 md:w-96 lg:w-[28rem] bg-transparent text-white placeholder-gray-500 border-b border-gray-700 outline-none focus:border-white transition-all duration-500 font-light text-sm tracking-wide"
                          style={{ fontSize: '16px' }}
                          autoFocus
                        />
                        
                        <button
                          type="button"
                          onClick={() => {
                            setMapSearchTerm('');
                            setSearchActive(false);
                          }}
                          className="absolute right-3 text-gray-500 hover:text-white p-1 transition-colors duration-300"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            )}            

            <nav className="flex items-center space-x-4 md:space-x-8 h-full">
              {/* Galaxy search mounts here (left of the menu) via portal;
                  everywhere else, the global SoulSearch renders in its place */}
              <div id="galaxy-header-slot" className="flex items-center">
                {!isGalaxy && <SoulSearch />}
              </div>
              {user && !isForgotPassword && !isMap && (
                <Link
                  to="/create-memorial"
                  className="hidden md:flex items-center px-6 py-3 text-xs uppercase tracking-widest transition-all duration-300 text-white hover:opacity-70 relative font-normal"
                >
                  <span className={`${isCreate ? 'border-b border-white pb-1' : ''}`}>
                    Create Memorial
                  </span>
                </Link>
              )}

              {user ? (
                !isForgotPassword && (
                  <div className="relative dropdown-container">
                    {/* User icon trigger - Squarespace style */}
                    <button
                      onClick={() => {
                        setUser(auth.currentUser);
                        setShowDropdown(v => !v);
                      }}
                      aria-label="User menu"
                      className="p-3 hover:opacity-70 transition-all duration-300"
                    >
                      {user?.photoURL ? (
                        <img
                          src={user.photoURL}
                          alt="Profile"
                          className="h-5 w-5 object-cover"
                        />
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg"
                             className="h-5 w-5 text-white"
                             fill="none" viewBox="0 0 24 24"
                             stroke="currentColor" strokeWidth={1}>
                          <path strokeLinecap="round" strokeLinejoin="round"
                                d="M5.121 17.804A9.966 9.966 0 0112 15c2.486 0 4.773.967 6.879 2.804M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>

                    {/* Backdrop overlay - INCREASED Z-INDEX */}
                    <div 
                      className={`backdrop fixed inset-0 bg-black/50 ${
                        showDropdown ? 'visible' : ''
                      }`}
                      style={{ zIndex: 99998 }}
                      onClick={() => {
                        setShowDropdown(false);
                        setShowDonationsSubmenu(false); // Reset donations submenu
                        setShowSettingsSubmenu(false); // Reset settings submenu
                      }}
                    />
                    
                    {/* Side drawer - Squarespace style - INCREASED Z-INDEX */}
                    <div className={`side-drawer fixed top-0 right-0 h-full w-96 max-w-[90vw] bg-black border-l border-gray-900 ${
                      showDropdown ? 'open' : ''
                    }`}
                    style={{ zIndex: 99999 }}>
                      {/* Drawer header */}
                      <div className="flex items-center justify-between h-20 md:h-24 px-6 border-b border-gray-900 bg-black">
                        <div className="flex items-center gap-3 flex-1 justify-start mr-4">
                          {user?.photoURL ? (
                            <img
                              src={user.photoURL}
                              alt="Profile"
                              className="w-10 h-10 md:w-12 md:h-12 object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 md:w-12 md:h-12 bg-white flex items-center justify-center text-black text-[11px] md:text-[13px] font-light flex-shrink-0 tracking-wider">
                              {getUserInitials(user)}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-[14px] font-light text-white truncate tracking-wide">{getUserDisplayName(user)}</p>
                            <p className="text-[11px] text-gray-500 truncate tracking-widest uppercase">{user.email}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setShowDropdown(false);
                            setShowDonationsSubmenu(false); // Reset donations submenu
                            setShowSettingsSubmenu(false); // Reset settings submenu
                          }}
                          className="p-2 hover:bg-gray-900 transition-all duration-500 flex-shrink-0 ml-2"
                        >
                          <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      
                      {/* Menu content - Squarespace style */}
                      <div className="overflow-y-auto h-[calc(100vh-5rem)] md:h-[calc(100vh-6rem)]">
                        <div className="p-8 pb-16 space-y-0.5">
                          <button
                            onClick={() => {
                              navigate('/create-memorial');
                              setShowDropdown(false);
                            }}
                            className={`menu-item w-full flex items-center px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 ${
                              showDropdown ? 'visible' : ''
                            }`}
                            style={{ transitionDelay: showDropdown ? '300ms' : '0ms' }}
                          >
                            <span className="text-[13px] tracking-[0.2em] uppercase font-light">Create Memorial</span>
                          </button>
                          
                          <button
                            onClick={() => {
                              navigate('/dashboard');
                              setShowDropdown(false);
                            }}
                            className={`menu-item w-full flex items-center px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 ${
                              showDropdown ? 'visible' : ''
                            }`}
                            style={{ transitionDelay: showDropdown ? '350ms' : '0ms' }}
                          >
                            <span className="text-[13px] tracking-[0.2em] uppercase font-light">Dashboard</span>
                          </button>
                          
                          <div className={`menu-item border-t border-gray-800/50 my-6 ${
                            showDropdown ? 'visible' : ''
                          }`}
                          style={{ transitionDelay: showDropdown ? '375ms' : '0ms' }}></div>
                          
                          <button
                            onClick={() => {
                              navigate('/subscription');
                              setShowDropdown(false);
                            }}
                            className={`menu-item w-full flex items-center px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 ${
                              showDropdown ? 'visible' : ''
                            }`}
                            style={{ transitionDelay: showDropdown ? '400ms' : '0ms' }}
                          >
                            <span className="text-[13px] tracking-[0.2em] uppercase font-light">Subscription</span>
                          </button>
                          
                          {/* Donations section - Collapsible */}
                          <div className={`menu-item ${
                            showDropdown ? 'visible' : ''
                          }`}
                          style={{ transitionDelay: showDropdown ? '450ms' : '0ms' }}>
                            <button
                              onClick={() => setShowDonationsSubmenu(!showDonationsSubmenu)}
                              className="w-full flex items-center justify-between px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 group"
                            >
                              <span className="text-[13px] tracking-[0.2em] uppercase font-light">Donations</span>
                              <svg 
                                className={`w-3.5 h-3.5 text-gray-500 transition-all duration-500 ${showDonationsSubmenu ? 'rotate-180' : ''} group-hover:text-gray-300`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            
                            {/* Donations submenu */}
                            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
                              showDonationsSubmenu ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                            }`}>
                              <div className="py-2">
                                <button
                                  onClick={() => {
                                    navigate('/donations');
                                    setShowDropdown(false);
                                  }}
                                  className="w-full flex items-center px-16 py-4 text-left text-gray-400 hover:text-white transition-all duration-500"
                                >
                                  <span className="text-[12px] tracking-[0.15em] uppercase font-light">Donation Collected</span>
                                </button>
                                <button
                                  onClick={() => {
                                    navigate('/payment-setup');
                                    setShowDropdown(false);
                                  }}
                                  className="w-full flex items-center px-16 py-4 text-left text-gray-400 hover:text-white transition-all duration-500"
                                >
                                  <span className="text-[12px] tracking-[0.15em] uppercase font-light">Payment Transfer</span>
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          <div className={`menu-item border-t border-gray-800/50 my-6 ${
                            showDropdown ? 'visible' : ''
                          }`}
                          style={{ transitionDelay: showDropdown ? '500ms' : '0ms' }}></div>
                          
                          <button
                            onClick={() => {
                              navigate('/faq');
                              setShowDropdown(false);
                            }}
                            className={`menu-item w-full flex items-center px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 ${
                              showDropdown ? 'visible' : ''
                            }`}
                            style={{ transitionDelay: showDropdown ? '550ms' : '0ms' }}
                          >
                            <span className="text-[13px] tracking-[0.2em] uppercase font-light">FAQ</span>
                          </button>
                          
                          <button
                            onClick={() => {
                              navigate('/help');
                              setShowDropdown(false);
                            }}
                            className={`menu-item w-full flex items-center px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 ${
                              showDropdown ? 'visible' : ''
                            }`}
                            style={{ transitionDelay: showDropdown ? '600ms' : '0ms' }}
                          >
                            <span className="text-[13px] tracking-[0.2em] uppercase font-light">Help</span>
                          </button>
                          
                          <button
                            onClick={() => {
                              navigate('/about');
                              setShowDropdown(false);
                            }}
                            className={`menu-item w-full flex items-center px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 ${
                              showDropdown ? 'visible' : ''
                            }`}
                            style={{ transitionDelay: showDropdown ? '650ms' : '0ms' }}
                          >
                            <span className="text-[13px] tracking-[0.2em] uppercase font-light">About Us</span>
                          </button>
                          
                          <button
                            onClick={() => {
                              navigate('/blog');
                              setShowDropdown(false);
                            }}
                            className={`menu-item w-full flex items-center px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 ${
                              showDropdown ? 'visible' : ''
                            }`}
                            style={{ transitionDelay: showDropdown ? '700ms' : '0ms' }}
                          >
                            <span className="text-[13px] tracking-[0.2em] uppercase font-light">Blog</span>
                          </button>
                          
                          <button
                            onClick={() => {
                              navigate('/community');
                              setShowDropdown(false);
                            }}
                            className={`menu-item w-full flex items-center px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 ${
                              showDropdown ? 'visible' : ''
                            }`}
                            style={{ transitionDelay: showDropdown ? '750ms' : '0ms' }}
                          >
                            <span className="text-[13px] tracking-[0.2em] uppercase font-light">Grief Support</span>
                          </button>
                          
                          <div className={`menu-item border-t border-gray-800/50 my-6 ${
                            showDropdown ? 'visible' : ''
                          }`}
                          style={{ transitionDelay: showDropdown ? '800ms' : '0ms' }}></div>
                          
                          {/* Settings section - Collapsible */}
                          <div className={`menu-item ${
                            showDropdown ? 'visible' : ''
                          }`}
                          style={{ transitionDelay: showDropdown ? '850ms' : '0ms' }}>
                            <button
                              onClick={() => setShowSettingsSubmenu(!showSettingsSubmenu)}
                              className="w-full flex items-center justify-between px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 group"
                            >
                              <span className="text-[13px] tracking-[0.2em] uppercase font-light">Settings</span>
                              <svg 
                                className={`w-3.5 h-3.5 text-gray-500 transition-all duration-500 ${showSettingsSubmenu ? 'rotate-180' : ''} group-hover:text-gray-300`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            
                            {/* Settings submenu */}
                            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
                              showSettingsSubmenu ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                            }`}>
                              <div className="py-2">
                                <button
                                  onClick={() => {
                                    navigate('/account-settings');
                                    setShowDropdown(false);
                                  }}
                                  className="w-full flex items-center px-16 py-4 text-left text-gray-400 hover:text-white transition-all duration-500"
                                >
                                  <span className="text-[12px] tracking-[0.15em] uppercase font-light">Account Settings</span>
                                </button>
                                <button
                                  onClick={() => {
                                    navigate('/account-security');
                                    setShowDropdown(false);
                                  }}
                                  className="w-full flex items-center px-16 py-4 text-left text-gray-400 hover:text-white transition-all duration-500"
                                >
                                  <span className="text-[12px] tracking-[0.15em] uppercase font-light">Account Security</span>
                                </button>
                                <button
                                  onClick={async () => {
                                    if (window.confirm('Are you sure you want to logout?')) {
                                      await handleLogout();
                                    }
                                  }}
                                  className="w-full flex items-center px-16 py-4 text-left text-red-400 hover:text-red-300 transition-all duration-500"
                                >
                                  <span className="text-[12px] tracking-[0.15em] uppercase font-light">Logout</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                !isLogin && !isSignup && (
                  <div className="flex items-center">
                    {/* Desktop: Log In and Sign Up buttons - Squarespace style */}
                    <div className="hidden md:flex items-center space-x-6 mr-6">
                      <Link
                        to={getAuthUrl('/login')}
                        className="px-2 py-3 text-xs uppercase tracking-widest text-white hover:opacity-70 transition-all duration-300 font-normal"
                      >
                        Log In
                      </Link>
                      <Link
                        to={getAuthUrl('/signup')}
                        className="px-6 py-2.5 text-xs bg-white text-black hover:bg-gray-100 transition-all duration-300 uppercase tracking-widest font-medium"
                      >
                        Get Started
                      </Link>
                    </div>

                    {/* Burger menu - Squarespace style */}
                    <div className="relative burger-menu-container">
                      <button
                        onClick={() => setShowBurgerMenu(!showBurgerMenu)}
                        aria-label="Menu"
                        className="p-3 hover:opacity-70 transition-all duration-300 flex items-center justify-center"
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          className="h-5 w-5 text-white" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor" 
                          strokeWidth={1}
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            d="M4 6h16M4 12h16M4 18h16" 
                          />
                        </svg>
                      </button>

                      {/* Backdrop overlay for burger menu - INCREASED Z-INDEX */}
                      <div 
                        className={`backdrop fixed inset-0 bg-black/50 ${
                          showBurgerMenu ? 'visible' : ''
                        }`}
                        style={{ zIndex: 99998 }}
                        onClick={() => setShowBurgerMenu(false)}
                      />
                      
                      {/* Side drawer for burger menu - Squarespace style - INCREASED Z-INDEX */}
                      <div className={`side-drawer fixed top-0 right-0 h-full w-96 max-w-[90vw] bg-black border-l border-gray-900 ${
                        showBurgerMenu ? 'open' : ''
                      }`}
                      style={{ zIndex: 99999 }}>
                        {/* Drawer header */}
                        <div className="flex items-center justify-between h-20 md:h-24 px-6 border-b border-gray-900 bg-black">
                          <h2 className="text-[14px] font-extralight text-white tracking-[0.2em] uppercase">Menu</h2>
                          <button
                            onClick={() => setShowBurgerMenu(false)}
                            className="p-2 hover:bg-gray-900 transition-all duration-500 flex-shrink-0 ml-4"
                          >
                            <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        
                        {/* Menu content */}
                        <div className="overflow-y-auto h-[calc(100vh-5rem)] md:h-[calc(100vh-6rem)]">
                          <div className="p-8 pb-16 space-y-2">
                            {/* Only show Log In and Sign Up on mobile */}
                            <div className="md:hidden">
                              <button
                                onClick={() => {
                                  navigate(getAuthUrl('/login'));
                                  setShowBurgerMenu(false);
                                }}
                                className={`menu-item w-full flex items-center px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 ${
                                  showBurgerMenu ? 'visible' : ''
                                }`}
                                style={{ transitionDelay: showBurgerMenu ? '300ms' : '0ms' }}
                              >
                                <span className="text-[13px] tracking-[0.2em] uppercase font-light">Log In</span>
                              </button>
                              
                              <button
                                onClick={() => {
                                  navigate(getAuthUrl('/signup'));
                                  setShowBurgerMenu(false);
                                }}
                                className={`menu-item w-full flex items-center px-8 py-5 transition-all duration-500 ${
                                  showBurgerMenu ? 'visible' : ''
                                }`}
                                style={{ transitionDelay: showBurgerMenu ? '350ms' : '0ms' }}
                              >
                                <div className="bg-white text-black px-6 py-2.5 text-xs uppercase tracking-widest font-medium hover:bg-gray-100 transition-all duration-300 inline-block">
                                  Get Started
                                </div>
                              </button>
                              
                              <div className={`menu-item border-t border-gray-800/50 my-6 ${
                                showBurgerMenu ? 'visible' : ''
                              }`}
                              style={{ transitionDelay: showBurgerMenu ? '400ms' : '0ms' }}></div>
                            </div>
                            
                            <button
                              onClick={() => {
                                navigate('/faq');
                                setShowBurgerMenu(false);
                              }}
                              className={`menu-item w-full flex items-center px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 ${
                                showBurgerMenu ? 'visible' : ''
                              }`}
                              style={{ transitionDelay: showBurgerMenu ? '450ms' : '0ms' }}
                            >
                              <span className="text-[13px] tracking-[0.2em] uppercase font-light">FAQ</span>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/help');
                                setShowBurgerMenu(false);
                              }}
                              className={`menu-item w-full flex items-center px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 ${
                                showBurgerMenu ? 'visible' : ''
                              }`}
                              style={{ transitionDelay: showBurgerMenu ? '500ms' : '0ms' }}
                            >
                              <span className="text-[13px] tracking-[0.2em] uppercase font-light">Help</span>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/about');
                                setShowBurgerMenu(false);
                              }}
                              className={`menu-item w-full flex items-center px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 ${
                                showBurgerMenu ? 'visible' : ''
                              }`}
                              style={{ transitionDelay: showBurgerMenu ? '550ms' : '0ms' }}
                            >
                              <span className="text-[13px] tracking-[0.2em] uppercase font-light">About Us</span>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/blog');
                                setShowBurgerMenu(false);
                              }}
                              className={`menu-item w-full flex items-center px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 ${
                                showBurgerMenu ? 'visible' : ''
                              }`}
                              style={{ transitionDelay: showBurgerMenu ? '600ms' : '0ms' }}
                            >
                              <span className="text-[13px] tracking-[0.2em] uppercase font-light">Blog</span>
                            </button>
                            
                            <button
                              onClick={() => {
                                navigate('/community');
                                setShowBurgerMenu(false);
                              }}
                              className={`menu-item w-full flex items-center px-8 py-5 text-left text-white hover:text-gray-300 transition-all duration-500 ${
                                showBurgerMenu ? 'visible' : ''
                              }`}
                              style={{ transitionDelay: showBurgerMenu ? '650ms' : '0ms' }}
                            >
                              <span className="text-[13px] tracking-[0.2em] uppercase font-light">Grief Support</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
            </nav>
          </div>
        </header>
      )}

      <main className={hideWrapper ? 'pt-0' : 'container mx-auto pt-28 md:pt-36 pb-10 px-6'}>
        <div className={hideWrapper ? '' : 'bg-white border border-gray-200 p-8'}>
          <React.Suspense fallback={null}>
          <Routes>
            {/* Public — the Galaxy of Memorials is the landing view */}
            <Route
              path="/"
              element={
<GalaxyEntry user={user} />
              }
            />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {/* Legal Pages */}
            <Route path="/legal/terms" element={<Terms />} />
            <Route path="/legal/privacy" element={<Privacy />} />
            <Route path="/legal/donation" element={<DonationAgreement />} />

            {/* Memorial - UPDATED TO USE SLUG */}
            <Route path="/memorial/:slug" element={<MemorialPage />} />
            <Route 
              path="/memorial/:slug/edit" 
              element={user ? <EditMemorialForm /> : <Navigate to="/login" replace />} 
            />

            {/* Search Results - NEW */}
            <Route path="/search" element={<SearchResultsPage />} />

            {/* Protected */}
            <Route 
              path="/dashboard" 
              element={user ? <Dashboard user={user} /> : <Navigate to="/login" replace />} 
            />
            <Route 
              path="/dashboard/:userId" 
              element={<Dashboard user={user} />} 
            />
            <Route
              path="/create-memorial"
              element={user ? <QuickCreate /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/forever"
              element={
                <React.Suspense fallback={<div className="min-h-screen" style={{ background: '#05040d' }} />}>
                  <Forever />
                </React.Suspense>
              }
            />
            <Route
              path="/forever/complete"
              element={
                user ? (
                  <React.Suspense fallback={<div className="min-h-screen" style={{ background: '#05040d' }} />}>
                    <ForeverComplete />
                  </React.Suspense>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/create-memorial/full"
              element={user ? <MemorialForm /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/account-settings"
              element={user ? <AccountSettings /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/account-security"
              element={user ? <AccountSecurity /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/payment-setup"
              element={user ? <PaymentSetup /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/donations"
              element={user ? <DonationDashboard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/subscription"
              element={user ? <SubscriptionPage /> : <Navigate to="/login" replace />}
            />
            
            {/* NEW: Memorial Selection and Edit Routes */}
            <Route
              path="/select-memorial"
              element={user ? <MemorialSelector /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/edit-memorial/:slug"
              element={user ? <EditMemorialForm /> : <Navigate to="/login" replace />}
            />
            
            {/* Legacy edit route - keep for backwards compatibility */}
            <Route path="/edit/:id" element={<EditMemorialForm />} />

            {/* NEW: FAQ, Help, and About pages - Public access */}
            <Route path="/faq" element={<FAQ />} />
            <Route path="/help" element={<Help />} />
            <Route path="/about" element={<About />} />
            
            {/* Blog - Public access */}
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            
            {/* Blog Admin - Protected */}
            <Route 
              path="/blog-admin" 
              element={user ? <BlogAdmin /> : <Navigate to="/login" replace />} 
            />
            <Route 
              path="/blog-admin/:postId" 
              element={user ? <BlogAdmin /> : <Navigate to="/login" replace />} 
            />

            {/* Community - Public for viewing, login required to post */}
            <Route path="/community" element={<CommunityList />} />
            <Route path="/community/:postId" element={<CommunityPost />} />
            <Route 
              path="/community-admin" 
              element={user ? <CommunityAdmin /> : <Navigate to="/login" replace />} 
            />

<Route path="/legal/community" element={<CommunityGuidelines />} />

            {/* Map & Sponsored */}
            <Route path="/map" element={<Navigate to="/" replace />} />
            <Route
              path="/galaxy"
              element={
<GalaxyEntry user={user} />
              }
            />
            {!user && <Route path="/sponsored" element={<SponsoredPage />} />}
{/* Catch-all route - redirect any unmatched routes to map */}
<Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </React.Suspense>
          {/* Conditionally render Footer only on FAQ, Help, and About pages */}
          {showFooter && <Footer />}
        </div>
      </main>
    </div>
  );
}

export default function AppWrapper() {
  return (
    <Router>
      <ScrollToTop />
      <AnalyticsRouteTracker />
      <App />
    </Router>
  );
}
